import {Edge, Node} from 'reactflow';

import {
  EntityMapEdgeNode,
  EntityMapEntityNodeOrInaccessibleInfoNode,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';

/**
 * Graph nodes and edges for an entity map graph, keyed by entity id.
 */
export interface GraphNodesAndEdgesById {
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>;
  graphEdgesById: Map<string, Edge>;
}

/**
 * Parses entity map entities and edges and converts them into react flow nodes and edges to return GraphNodesAndEdgesById
 */
export function parseEntityMapIntoGraphNodesAndEdgesById({
  entities,
  edges,
}: {
  entities: Array<EntityMapEntityNodeOrInaccessibleInfoNode>;
  edges: Array<EntityMapEdgeNode>;
}): GraphNodesAndEdgesById {
  const graphNodesById = new Map(
    entities.map((entity) => entityMapEntityNodeToReactFlowNode(entity)).map((node) => [node.id, node])
  );
  const graphEdgesById = new Map(edges.map((edge) => [edge.id, entityMapEdgeNodeToReactFlowEdge(edge)]));
  return {graphNodesById, graphEdgesById};
}

/**
 * Converts an EntityMapEntityNode to a ReactFlow Node
 *
 * @param entity The EntityMapEntityNode to convert
 * @returns The ReactFlow Node
 */
export function entityMapEntityNodeToReactFlowNode(
  entity: EntityMapEntityNodeOrInaccessibleInfoNode
): Node<EntityMapEntityNodeOrInaccessibleInfoNode> {
  return {
    id: isInaccessible(entity) ? entity.invalidId : entity.id,
    type: 'entity',
    data: entity,
    position: {x: 0, y: 0},
  };
}

/**
 * Converts an EntityMapEdgeNode to a ReactFlow Edge
 *
 * @param edge The EntityMapEdgeNode to convert
 * @returns The ReactFlow Edge
 */
export function entityMapEdgeNodeToReactFlowEdge(edge: EntityMapEdgeNode): Edge {
  return {
    id: edge.id,
    source: edge.sourceId,
    target: edge.targetId,
    label: 'entity link', // TODO(BNCH-126927): Update with the correct label
    ...getTestProps({component: 'EntityMapEdge', key: edge.id}),
  };
}
